import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Box,
  Menu,
  Flex,
  Image,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import Dropzone from 'react-dropzone';
import { useState } from 'react';
import moment from 'moment';

import FormField from 'components/FormField';
import TextArea from 'components/Textarea';
import UpdateBlockSchema from 'validation/schemas/UpdateBlockSchema';
import Pencil from 'components/Icons/Pencil';
import { handleFileDelete, handleFileUpload } from 'services/s3Service';
import FullScreenLoader from 'components/FullScreenLoader';
import { acceptedFormats } from 'utils/FileFormats';

import blockImage from '../../assets/blox_logo.png';

interface Properties {
  isOpen: boolean;
  blockData: any;
  onClose: () => void;
  onSave: (form: any, blockData: any) => void;
}

const ModelModal = ({ isOpen, onClose, onSave, blockData }: Properties) => {
  const [loader, setLoader] = useState(false);

  const handleSubmit = async (form: any) => {
    const { name: formName, ...payload } = form;
    onSave(formName === blockData?.name ? payload : form, blockData);
  };

  const formatDate = (date: string) => {
    return moment(date).format('DD MMMM YYYY');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} blockScrollOnMount={false} isCentered>
      <ModalOverlay />
      <ModalContent p={3} maxW={'50rem'}>
        <Formik
          initialValues={{
            name: blockData?.name || '',
            description: blockData?.description || '',
            icon: blockData?.icon || '',
          }}
          onSubmit={(form: any) => handleSubmit(form)}
          validationSchema={UpdateBlockSchema}
        >
          {({ errors, values, touched, setFieldValue }: any) => {
            return (
              <Form>
                <ModalHeader position='relative' px={6} pt={3} pb={0}>
                  <Text variant='modals'>Block properties</Text>
                </ModalHeader>
                <>
                  <ModalBody>
                    <Flex gap={4} color='black'>
                      <Flex alignItems={'flex-start'} gap='5' width={'100%'} flexDir={'column'}>
                        <FormField
                          label='Block name'
                          name='name'
                          type='text'
                          labelStyles={{
                            fontSize: '15px',
                            color: '#212121',
                          }}
                          inputStyles={{
                            maxWidth: '300px',
                            boxShadow: '0px 0px 0px 1.5px #EEF1F6',
                            height: '35px',
                            paddingInlineStart: '8px',
                          }}
                          error={errors.name}
                          touched={touched.name}
                        />
                        <TextArea label='Block description' name='description' />
                        <Box mt={4}>
                          <Flex mb={1}>
                            <Text fontSize={'16px'} color='black'>
                              Created:{' '}
                            </Text>
                            <Text fontSize={'16px'} color={'violet.700'} ml={2}>
                              {formatDate(blockData?.createdAt)}
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>
                      <Flex minWidth={'330px'} flexDir='column'>
                        <Text mb={2}>Thumbnail</Text>
                        <Flex
                          height={'100%'}
                          width={'100%'}
                          position={'relative'}
                          justifyContent={'end'}
                        >
                          {loader ? (
                            <FullScreenLoader
                              bgColor='linear-gradient(#FFFFFF, #F3F3F3)'
                              style={{ position: 'absolute' }}
                              height='100%'
                              width='100%'
                            />
                          ) : (
                            <Image
                              position={'absolute'}
                              borderRadius={'12px'}
                              height={'100%'}
                              width={'100%'}
                              bg={'linear-gradient(#FFFFFF, #F3F3F3)'}
                              src={values?.icon || blockImage}
                            />
                          )}
                          <Menu variant='primary' placement='bottom-end'>
                            <MenuButton
                              height={'fit-content'}
                              zIndex={2}
                              mr={4}
                              mt={2}
                              type='button'
                            >
                              <Pencil
                                width={5}
                                height={6}
                                color='#bdbdbd'
                                style={{
                                  cursor: 'pointer',
                                }}
                              />
                            </MenuButton>
                            <MenuList minWidth={'160px !important'} zIndex={2}>
                              <MenuItem>
                                <Dropzone
                                  accept={acceptedFormats}
                                  onDrop={async (file: any) => {
                                    setLoader(true);
                                    const data = await handleFileUpload(
                                      file[0],
                                      'blox-user-assets',
                                    );
                                    setLoader(false);
                                    setFieldValue('icon', data);
                                  }}
                                  multiple={false}
                                >
                                  {({ getRootProps, getInputProps }) => (
                                    <section style={{ width: '100%' }}>
                                      <div style={{ width: '100%' }} {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <p>Update</p>
                                      </div>
                                    </section>
                                  )}
                                </Dropzone>
                              </MenuItem>
                              {values.icon && (
                                <MenuItem
                                  onClick={async () => {
                                    setLoader(true);
                                    await handleFileDelete(values?.icon, 'blox-user-assets').then(
                                      (response: any) => {
                                        if (!response?.error) {
                                          setFieldValue('icon', '');
                                        }
                                      },
                                    );
                                    setLoader(false);
                                  }}
                                >
                                  Remove image
                                </MenuItem>
                              )}
                            </MenuList>
                          </Menu>
                        </Flex>
                      </Flex>
                    </Flex>
                  </ModalBody>
                  <ModalFooter pb='0'>
                    <HStack spacing='5'>
                      <Button
                        px={'10'}
                        w='fit-content'
                        h={'10'}
                        borderRadius='10px'
                        fontSize='xs'
                        color='violet.800'
                        bgColor='grey.500'
                        onClick={onClose}
                      >
                        CANCEL
                      </Button>
                      <Button
                        px={'10'}
                        _hover={{ _disabled: {} }}
                        type='submit'
                        w='fit-content'
                        h={'10'}
                        borderRadius='10px'
                        fontSize='xs'
                        bgColor='violet.800'
                        disabled={loader}
                      >
                        SAVE
                      </Button>
                    </HStack>
                  </ModalFooter>
                </>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default ModelModal;
